import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarClock,
  Download,
  Gauge,
  Pencil,
  Link2Off,
  MapPin,
  Timer,
  TimerOff,
  TrendingDown,
  UserX,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardRow,
  ChartFrame,
  DataTable,
  EmptyState,
  ErrorState,
  InfoHint,
  PageHeader,
  Skeleton,
  StatCard,
  StatIcon,
  StatusPill,
  toast,
  type DataTableColumn,
} from '../../components/ui';
import { ApiError } from '../../lib/api';
import { countryName } from '../../lib/countries';
import { cn } from '../../lib/cn';
import {
  formatDate,
  formatDateShort,
  formatHours,
  formatMoney,
  formatMonth,
  formatPercent,
} from '../../lib/format';
import { usePersonDetail, useSetTimePolicy, type PersonDetailResponse, type PersonMonthRow } from './api';
import {
  LinkAccountModal,
  TimePolicyGlyph,
  cadencePhrase,
  defaultCadenceFor,
  timePolicyLabel,
} from './time-policy';
import {
  Avatar,
  CostModelChip,
  InProgressPill,
  TypeBadge,
  UTILIZATION_TARGET,
  UtilizationChartDefs,
  UtilizationLegend,
  downloadCsv,
  formatHoursFixed,
  utilizationChartFill,
  utilizationLabel,
  utilizationTextHex,
} from './components';
import { PersonModal } from './PersonModal';
import { PersonMonthDialog } from './PersonMonthDialog';

// ---------------------------------------------------------------------------
// Utilization chart (12 mo of billable ÷ capacity, on the utilization ramp)
// ---------------------------------------------------------------------------

function shortMonth(key: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(
    new Date(`${key}-01T12:00:00Z`),
  );
}

/**
 * Twelve month ticks do not fit in 390px — Recharts renders them anyway and
 * they collide into "SepOctNoDecJanFebMarAprMayJunJulAug". Below the breakpoint
 * we label every other month (and always the last one, which is the month the
 * reader came for) so the axis stays a scale instead of a smear.
 */
function useTickInterval(): number {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow ? 1 : 0;
}

function UtilizationChart({ months }: { months: PersonMonthRow[] }) {
  const tickInterval = useTickInterval();
  const data = months.map((m) => ({
    label: shortMonth(m.month),
    month: m.month,
    ratio: m.utilization,
    pct: m.utilization != null ? Math.round(m.utilization * 1000) / 10 : null,
    hours: m.hours,
    billableHours: m.billableHours,
    capacityHours: m.capacityHours,
    billableShare: m.billableShare,
    inProgress: m.inProgress,
  }));
  const hasData = data.some((d) => d.pct != null);

  if (!hasData) {
    return (
      <EmptyState
        icon={<Gauge />}
        title="No capacity to measure against"
        description="Set a weekly capacity on this person — utilization is billable hours ÷ available hours."
      />
    );
  }

  const peak = Math.max(100, ...data.map((d) => d.pct ?? 0));
  const top = Math.ceil(peak / 25) * 25;
  const ticks: number[] = [];
  for (let t = 0; t <= top; t += 25) ticks.push(t);

  return (
    // The plot grows into whatever the card has left after its legend and stats
    // rail, with `min="sm"` as the readable floor — it used to be a flat `h-52`
    // regardless of the card's height.
    <ChartFrame min="sm" className="relative min-w-0">
      <UtilizationChartDefs />
      <ResponsiveContainer width="100%" height="100%">
        {/* Right margin holds the target label so it can never be drawn behind
            the bars and chopped into an illegible ghost. */}
        <BarChart
          data={data}
          margin={{ top: 8, right: 62, bottom: 0, left: 0 }}
          barCategoryGap="18%"
        >
          <CartesianGrid vertical={false} stroke="#eef0f3" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: '#e2e5e9' }}
            tick={{ fill: '#6c7683', fontSize: 11 }}
            interval={0}
            // Anchored on the LAST month so the month the reader came for is
            // always labelled, and every other one drops out going backwards.
            tickFormatter={(value: string, index: number) =>
              tickInterval === 0 || (data.length - 1 - index) % (tickInterval + 1) === 0
                ? value
                : ''
            }
          />
          <YAxis
            domain={[0, top]}
            ticks={ticks}
            width={38}
            tickFormatter={(v: number) => `${v}%`}
            tickLine={false}
            axisLine={false}
            tick={{ fill: '#6c7683', fontSize: 11 }}
          />
          {/* Pinned under the plot area: a floating tooltip on a 12-bar chart
              covers half the series. Here it occludes nothing and never jumps. */}
          <Tooltip
            cursor={{ fill: 'rgba(20, 32, 51, 0.04)' }}
            allowEscapeViewBox={{ x: false, y: true }}
            position={{ y: 206 }}
            offset={12}
            wrapperStyle={{ pointerEvents: 'none', zIndex: 20 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0]!.payload as (typeof data)[number];
              return (
                <div className="card w-[16.5rem] px-3 py-2 text-body-sm shadow-elevated">
                  <p className="font-semibold text-secondary">
                    {formatMonth(`${d.month}-01`)}
                    {d.inProgress && <span className="ml-1 text-grey-500">· to date</span>}
                  </p>
                  <p className="num font-semibold" style={{ color: utilizationTextHex(d.ratio) }}>
                    {d.pct != null ? formatPercent(d.pct, { already100: true }) : '—'} of capacity
                    <span className="ml-1 font-normal">· {utilizationLabel(d.ratio)}</span>
                  </p>
                  <p className="num text-grey-500">
                    {formatHoursFixed(d.billableHours)} billable /{' '}
                    {formatHoursFixed(d.capacityHours)} h capacity
                  </p>
                  <p className="num text-grey-500">
                    {formatHoursFixed(d.hours)} h tracked
                    {d.billableShare != null
                      ? ` · ${formatPercent(d.billableShare, { decimals: 0 })} billable`
                      : ''}
                  </p>
                </div>
              );
            }}
          />
          <Bar dataKey="pct" radius={[4, 4, 0, 0]} maxBarSize={26}>
            {data.map((d) => (
              <Cell key={d.month} fill={utilizationChartFill(d.ratio)} />
            ))}
          </Bar>
          {/* After <Bar> so the line and its label paint on top of the bars. */}
          <ReferenceLine
            y={UTILIZATION_TARGET * 100}
            stroke="#142033"
            strokeDasharray="4 4"
            strokeOpacity={0.55}
            label={{
              value: `${Math.round(UTILIZATION_TARGET * 100)}% target`,
              position: 'right',
              fill: '#6c7683',
              fontSize: 10,
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

// ---------------------------------------------------------------------------
// Hours by agreement (horizontal bars, billable split, click through)
// ---------------------------------------------------------------------------

/**
 * **THE TAG THAT DECIDES WHO SEES A TIMER.**
 *
 * Having a person record is not the same as tracking time: directory sync makes
 * one for everyone on the agency roster — sales, admin, whoever — so before this
 * existed the gate was a side effect of a sync rather than a decision, and half
 * the roster was being offered a clock.
 *
 * ⚠ It lives HERE, on the person, not in accounts: an app grant is deliberately
 * not required to log an hour (that is the whole point of the timer's own API),
 * so entitlement is the wrong place to express it. Financials owns time and owns
 * `Person`.
 *
 * ⚠ It is NOT "is a timesheet expected" — that is the Approvals roster's control
 * and it means something else. An owner who logs the occasional hour tracks time
 * and has asked not to be chased; both states are real, so both are settings.
 */
function TracksTimeToggle({ person }: { person: PersonDetailResponse['person'] }) {
  const setPolicy = useSetTimePolicy();
  const on = person.tracksTime;
  /**
   * ⚠ Tracking needs an account (the timer signs in as the person, and the
   * server refuses to TURN IT ON without one). An unlinked person who is not
   * tracking gets no toggle here — the "no Solid account" chip carries the one
   * action that unblocks it (Link account), so the verb is not printed twice.
   *
   * ⚠ An unlinked person who IS tracking (a Harvest-era row that arrived that
   * way) keeps "Turn off time tracking": they are still chased on their
   * cadence while no timer can reach them, and the server accepts
   * `tracksTime: false` without an account. Link the account, or turn it off —
   * both ways out stay on this header.
   */
  if (!on && !person.hasSolidAccount) return null;
  const cadence = defaultCadenceFor(person.type);
  return (
    <Button
      variant="outline"
      disabled={setPolicy.isPending}
      onClick={() =>
        setPolicy.mutate(
          on
            ? { id: person.id, tracksTime: false, timesheetCadence: 'NEVER' }
            : { id: person.id, tracksTime: true, timesheetCadence: cadence },
          {
            onSuccess: () =>
              toast.success(
                on
                  ? person.hasSolidAccount
                    ? `${person.name} no longer tracks time — the timer is hidden for them everywhere.`
                    : `${person.name} no longer tracks time — no timesheet is chased from them.`
                  : `${person.name} tracks time and ${cadencePhrase(cadence)} — change the frequency under Edit.`,
              ),
            onError: (error: unknown) =>
              toast.error(error instanceof Error ? error.message : 'Could not change that.'),
          },
        )
      }
    >
      {/*
        ⚠ THE LABEL IS THE ACTION, NOT THE STATE. The state lives in the chip
        row, where every other fact about this person lives; a status in an
        outline button beside "Edit" read as a label nobody could press.
      */}
      {on ? (
        <>
          <TimerOff className="h-4 w-4" aria-hidden /> Turn off time tracking
        </>
      ) : (
        <>
          <Timer className="h-4 w-4" aria-hidden /> Turn on time tracking
        </>
      )}
    </Button>
  );
}

function AgreementBars({ agreements }: { agreements: PersonDetailResponse['agreements'] }) {
  const top = agreements.slice(0, 8);
  const max = Math.max(...top.map((p) => p.hours), 1);
  if (top.length === 0) {
    return (
      <EmptyState
        icon={<Timer />}
        title="No agreement hours"
        description="Hours land here as time is tracked to agreements."
      />
    );
  }
  return (
    // This component's half of the app-wide fill rule: `<CardBody fill>` hands
    // it the row's height, and it spends it by letting the LIST take the slack
    // (`flex-1 justify-between`) rather than the legend, which must stay
    // attached to the bars it labels. Bars spread a few pixels; no dead band.
    <div className="flex flex-col">
      <div className="mb-3 flex items-center gap-4 text-caption text-grey-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-accent" aria-hidden /> Billable
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-grey-300" aria-hidden /> Non-billable
        </span>
      </div>
      <ul className="flex flex-1 flex-col justify-between gap-3">
        {top.map((p) => {
          const billablePct = (p.billableHours / max) * 100;
          const nonBillablePct = ((p.hours - p.billableHours) / max) * 100;
          return (
            <li key={p.agreementId}>
              <Link
                to={`/agreements/${p.agreementId}`}
                className="group block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <p className="min-w-0 truncate text-body-sm font-semibold text-secondary group-hover:text-accent">
                    {p.agreementName}
                    <span className="ml-1.5 font-normal text-grey-500">{p.clientName}</span>
                  </p>
                  <p className="num shrink-0 text-body-sm text-secondary-700">
                    {formatHoursFixed(p.hours)} h
                    <span className="ml-2 hidden text-grey-500 sm:inline">
                      {formatMoney(p.revenue, { whole: true })}
                    </span>
                  </p>
                </div>
                <div
                  className="flex h-2 w-full gap-px overflow-hidden rounded-full bg-grey-100"
                  role="img"
                  aria-label={`${p.agreementName}: ${formatHours(p.billableHours)} billable of ${formatHours(p.hours)} hours`}
                >
                  {p.billableHours > 0 && (
                    <span
                      className="h-full rounded-l-full bg-accent"
                      style={{ width: `${billablePct}%` }}
                    />
                  )}
                  {p.hours - p.billableHours > 0.001 && (
                    <span className="h-full bg-grey-300" style={{ width: `${nonBillablePct}%` }} />
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
      {agreements.length > top.length && (
        <p className="mt-3 text-caption text-grey-500">
          + {agreements.length - top.length} more agreement{agreements.length - top.length === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function DetailSkeleton() {
  return (
    <div className="space-y-6" aria-busy>
      <div className="flex items-center gap-4">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-72" />
        <Skeleton className="h-72" />
      </div>
      <Skeleton className="h-80" />
    </div>
  );
}

export function TeamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detail = usePersonDetail(id);
  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  // The month open in the breakdown lives in the URL — a row is a real link,
  // so it is tab-reachable, cmd-clickable and shareable, and Back closes it.
  const [params, setParams] = useSearchParams();
  const openMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(params.get('month') ?? '') ? params.get('month') : null;
  const monthHref = (key: string) => `/team/${id}?month=${key}`;
  const closeMonth = () => {
    const next = new URLSearchParams(params);
    next.delete('month');
    setParams(next, { replace: true });
  };

  const monthRows = useMemo(
    () => (detail.data ? [...detail.data.months].reverse() : []),
    [detail.data],
  );

  if (detail.isPending) {
    return (
      <div>
        <Link
          to="/team"
          className="mb-4 inline-flex items-center gap-1.5 text-body-sm font-semibold text-grey-500 hover:text-secondary"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Team
        </Link>
        <DetailSkeleton />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    // A 404 is not a transient failure — retrying can never fix it, so offer
    // the way back to the list instead of a dead "Try again".
    const notFound = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <div>
        <Link
          to="/team"
          className="mb-4 inline-flex items-center gap-1.5 text-body-sm font-semibold text-grey-500 hover:text-secondary"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Team
        </Link>
        <PageHeader title={notFound ? 'Person not found' : 'Team member'} />
        {notFound ? (
          <Card>
            <EmptyState
              icon={<UserX />}
              title="We couldn’t find that person"
              description="They may have been deleted, or the link points at another organization."
              action={
                <Button onClick={() => navigate('/team')}>
                  <ArrowLeft className="h-4 w-4" aria-hidden /> Back to the team list
                </Button>
              }
            />
          </Card>
        ) : (
          <ErrorState
            card
            message={
              detail.error instanceof Error ? detail.error.message : 'Failed to load this person'
            }
            onRetry={() => void detail.refetch()}
          />
        )}
      </div>
    );
  }

  const { person, months, totals, agreements, recentEntries, trailing3, negativeMonthStreak } =
    detail.data;
  const currentMonth = months[months.length - 1]!;

  const exportMonths = () =>
    downloadCsv(`${person.name.replace(/[^\w]+/g, '-').toLowerCase()}-monthly.csv`, [
      [
        'Month',
        'Tracked hours',
        'Billable hours',
        'Capacity hours',
        'Utilization (billable/capacity)',
        'Billable share (billable/tracked)',
        'Revenue',
        'Cost',
        'Margin',
        'In progress',
      ],
      ...months.map((m) => [
        m.month,
        m.hours.toFixed(2),
        m.billableHours.toFixed(2),
        m.capacityHours.toFixed(2),
        m.utilization != null ? m.utilization.toFixed(4) : '',
        m.billableShare != null ? m.billableShare.toFixed(4) : '',
        m.revenue.toFixed(2),
        m.cost.toFixed(2),
        m.margin.toFixed(2),
        m.inProgress ? 'yes' : '',
      ]),
      [
        '12-month total',
        totals.hours.toFixed(2),
        totals.billableHours.toFixed(2),
        totals.capacityHours.toFixed(2),
        totals.utilization != null ? totals.utilization.toFixed(4) : '',
        totals.billableShare != null ? totals.billableShare.toFixed(4) : '',
        totals.revenue.toFixed(2),
        totals.cost.toFixed(2),
        totals.margin.toFixed(2),
        '',
      ],
    ]);

  const monthColumns: Array<DataTableColumn<PersonMonthRow>> = [
    {
      key: 'month',
      header: 'Month',
      className: 'whitespace-nowrap px-3 sm:px-4',
      render: (m) => (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold text-secondary">{formatMonth(`${m.month}-01`)}</span>
          {m.inProgress && <InProgressPill />}
          {/* Hours ride along on mobile so the column itself can drop out and
              leave room for utilization — the thesis of this page. */}
          <span className="num w-full text-caption text-grey-500 sm:hidden">
            {formatHoursFixed(m.hours)} h tracked
          </span>
        </span>
      ),
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      className: 'hidden px-3 sm:table-cell sm:px-4',
      render: (m) => formatHoursFixed(m.hours),
    },
    {
      key: 'capacity',
      header: 'Capacity',
      align: 'right',
      className: 'hidden xl:table-cell',
      render: (m) =>
        m.capacityHours > 0 ? (
          formatHoursFixed(m.capacityHours)
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    {
      key: 'utilization',
      header: 'Util.',
      align: 'right',
      className: 'px-3 sm:px-4',
      render: (m) =>
        m.utilization != null ? (
          <span className="font-semibold" style={{ color: utilizationTextHex(m.utilization) }}>
            {formatPercent(m.utilization, { decimals: 0 })}
          </span>
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    {
      key: 'billable',
      header: 'Billable %',
      align: 'right',
      className: 'hidden md:table-cell',
      render: (m) =>
        m.billableShare != null ? (
          formatPercent(m.billableShare, { decimals: 0 })
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    {
      key: 'revenue',
      header: 'Revenue',
      align: 'right',
      className: 'hidden px-3 sm:table-cell sm:px-4',
      render: (m) => formatMoney(m.revenue),
    },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      className: 'hidden sm:table-cell',
      render: (m) => formatMoney(m.cost),
    },
    {
      key: 'margin',
      header: 'Margin',
      align: 'right',
      className: 'px-3 sm:px-4',
      render: (m) => (
        <span className={m.margin < 0 ? 'num-negative font-semibold' : 'font-semibold'}>
          {formatMoney(m.margin, { parens: true })}
        </span>
      ),
    },
  ];

  const entryColumns: Array<DataTableColumn<PersonDetailResponse['recentEntries'][number]>> = [
    {
      key: 'date',
      header: 'Date',
      className: 'whitespace-nowrap px-3 sm:px-4',
      render: (e) => (
        <>
          <span className="sm:hidden">{formatDateShort(e.date)}</span>
          <span className="hidden sm:inline">{formatDate(e.date)}</span>
        </>
      ),
    },
    {
      key: 'agreement',
      header: 'Agreement',
      className: 'max-w-[10rem] px-3 sm:max-w-[16rem] sm:px-4',
      render: (e) => (
        <span className="block min-w-0">
          <span className="block truncate font-semibold text-secondary">{e.agreementName}</span>
          <span className="block truncate text-caption text-grey-500">{e.clientName}</span>
        </span>
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      className: 'hidden md:table-cell max-w-[16rem]',
      render: (e) => <span className="block truncate text-grey-600">{e.notes ?? '—'}</span>,
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      className: 'px-3 sm:px-4',
      render: (e) => <span className="font-semibold">{formatHoursFixed(e.hours)}</span>,
    },
    {
      key: 'flags',
      header: <span className="sr-only">Billing state</span>,
      align: 'right',
      className: 'px-2 sm:px-4',
      // Abbreviated on mobile so the pill cannot push the agreement column into
      // an unreadable stub (or off the right edge of a 390px screen).
      render: (e) => {
        const [status, long, short] = e.invoiced
          ? (['paid', 'Invoiced', 'Inv'] as const)
          : e.billable
            ? (['open', 'Billable', 'Bill'] as const)
            : (['neutral', 'Non-bill', 'Non'] as const);
        return (
          <>
            <span className="sm:hidden" title={long}>
              <StatusPill status={status} label={short} dot={false} className="px-1.5" />
            </span>
            <span className="hidden sm:inline">
              <StatusPill status={status} label={long} dot={false} />
            </span>
          </>
        );
      },
    },
  ];

  const trailingLabel = `last ${trailing3.months} mo`;

  return (
    <div>
      <Link
        to="/team"
        className="mb-4 inline-flex items-center gap-1.5 text-body-sm font-semibold text-grey-500 transition-colors hover:text-secondary"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Team
      </Link>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar name={person.name} size="lg" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-heading-2 text-secondary">{person.name}</h1>
              <TypeBadge type={person.type} />
              {!person.isActive && <StatusPill status="neutral" label="Archived" dot={false} />}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-grey-500">
              <span>{person.email}</span>
              {person.startDate && (
                <span className="inline-flex items-center gap-1">
                  <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                  since {formatDate(person.startDate)}
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center gap-1 rounded-md border border-accent/30 bg-accent/5 px-2 py-0.5 text-xs font-semibold text-accent-700 num"
                title="Default bill rate"
              >
                {person.defaultBillRate != null
                  ? `${formatMoney(person.defaultBillRate, { whole: true })}/hr`
                  : 'No'}{' '}
                bill rate
              </span>
              <CostModelChip {...person} />
              {/* State belongs with the other facts about this person; the
                  button beside Edit is the verb. */}
              {/* The time policy: whether they track, and how often a sheet
                  is expected. */}
              {person.tracksTime && !person.hasSolidAccount ? (
                /* ⚠ TRACKING, WITH NO ACCOUNT. The server refuses to turn
                   tracking ON without an account, but Harvest-era rows arrived
                   already tracking and unlinked — and they are still chased on
                   their cadence while no timer can sign in as them. One amber
                   chip says so, carries the fix (Link account) and puts the
                   consequence behind its (i); "Turn off time tracking" beside
                   Edit is the other way out. It replaces BOTH ordinary chips,
                   so the missing account is said once. */
                <span className="inline-flex max-w-full flex-wrap items-center gap-x-1 gap-y-0.5 rounded-md border border-status-partial-border bg-status-partial-bg px-2 py-0.5 text-xs font-semibold text-status-partial">
                  <Link2Off className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>{timePolicyLabel(person)}</span>
                  <InfoHint label="Tracks time, with no Solid account" size="sm" width={300}>
                    <p>
                      {person.name} is marked as tracking time, but has no
                      accounts.soliddigital.com account linked. The timer signs in as the person, so
                      no timer shows for them in the app bar or the hub.
                    </p>
                    <p className="mt-2">
                      {person.timesheetCadence === 'NEVER'
                        ? 'No timesheet is chased from them.'
                        : `The reminder email still chases their ${person.timesheetCadence.toLowerCase()} timesheet, and missed periods still show on Approvals.`}
                    </p>
                    <p className="mt-2">
                      Link their account so they can log time, or turn time tracking off.
                    </p>
                  </InfoHint>
                  <button
                    type="button"
                    onClick={() => setLinkOpen(true)}
                    className="font-semibold text-accent-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                  >
                    Link account
                  </button>
                </span>
              ) : (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-semibold',
                    person.tracksTime
                      ? 'border-accent/30 bg-accent/5 text-accent-700'
                      : 'border-grey-200 bg-grey-50 text-grey-500',
                  )}
                >
                  <TimePolicyGlyph tracksTime={person.tracksTime} />
                  {timePolicyLabel(person)}
                </span>
              )}
              {/* Where they work from. Required of everyone who tracks time
                  (the R&D credit counts US research only); a tracker without
                  one is amber and offers the fix. */}
              {person.hasAddress ? (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-grey-200 bg-grey-50 px-2 py-0.5 text-xs font-semibold text-grey-600"
                  title={[
                    person.address.line1,
                    person.address.line2,
                    [person.address.city, person.address.region, person.address.postalCode].filter(Boolean).join(' '),
                    countryName(person.address.country),
                  ]
                    .filter(Boolean)
                    .join(', ')}
                >
                  <MapPin className="h-3.5 w-3.5" aria-hidden />
                  {[person.address.city, countryName(person.address.country)].filter(Boolean).join(', ')}
                </span>
              ) : person.tracksTime ? (
                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-status-partial-border bg-status-partial-bg px-2 py-0.5 text-xs font-semibold text-status-partial">
                  <MapPin className="h-3.5 w-3.5" aria-hidden />
                  no address — required to track time
                  <button
                    type="button"
                    onClick={() => setEditOpen(true)}
                    className="ml-1 font-semibold text-accent-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                  >
                    Add address
                  </button>
                </span>
              ) : null}
              {/* An unlinked person is worth marking: it is how you tell a
                  current colleague from a historical Harvest row. (A tracking
                  one is marked by the amber chip above instead.) */}
              {!person.hasSolidAccount && !person.tracksTime && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-grey-200 bg-grey-50 px-2 py-0.5 text-xs font-semibold text-grey-500">
                  <Link2Off className="h-3.5 w-3.5" aria-hidden />
                  no Solid account
                  <button
                    type="button"
                    onClick={() => setLinkOpen(true)}
                    className="ml-1 font-semibold text-accent-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                  >
                    Link account
                  </button>
                </span>
              )}
              <span
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-grey-200 bg-grey-50 px-2 py-0.5 text-xs font-semibold text-secondary-700 num"
                title="Contracted availability per week — the utilization denominator"
              >
                {person.weeklyCapacityHours ? `${formatHours(person.weeklyCapacityHours)} h/wk` : 'No'}
                <span className="font-medium text-grey-400">capacity</span>
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TracksTimeToggle person={person} />
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4" aria-hidden /> Edit
          </Button>
        </div>
      </div>

      {/* Margin trend callout — the 12-month headline must not read healthy
          while the recent months bleed. */}
      {negativeMonthStreak >= 2 && (
        <div
          className="mb-6 flex items-start gap-3 rounded-xl border border-status-late-border bg-status-late-bg px-4 py-3"
          role="status"
        >
          <TrendingDown className="mt-0.5 h-5 w-5 shrink-0 text-status-late" aria-hidden />
          <p className="text-body-sm text-secondary-800">
            <span className="font-bold text-status-late">
              Margin-negative {negativeMonthStreak} months running.
            </span>{' '}
            The {trailingLabel} total is{' '}
            <span className="num font-semibold">
              {formatMoney(trailing3.margin, { parens: true })}
            </span>{' '}
            on {formatMoney(trailing3.revenue, { whole: true })} of revenue against{' '}
            {formatMoney(trailing3.cost, { whole: true })} of cost — check bill rates and the
            non-billable split below.
          </p>
        </div>
      )}

      {/* KPI strip */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        <StatCard
          label={`Hours · ${shortMonth(currentMonth.month)}`}
          value={formatHours(currentMonth.hours)}
          hint={`${formatHours(currentMonth.billableHours)} billable`}
          icon={<StatIcon kind="hours" />}
        />
        <StatCard
          label="Utilization · 12 mo"
          value={
            totals.utilization != null ? (
              <span style={{ color: utilizationTextHex(totals.utilization) }}>
                {formatPercent(totals.utilization, { decimals: 0 })}
              </span>
            ) : (
              '—'
            )
          }
          hint={
            totals.capacityHours > 0
              ? `${formatHours(totals.capacityHours)} h capacity`
              : 'no weekly capacity set'
          }
          icon={<StatIcon kind="utilization" />}
        />
        <StatCard
          label="Revenue · 12 mo"
          value={formatMoney(totals.revenue, { whole: true })}
          hint="at bill rates"
          icon={<StatIcon kind="revenue" />}
        />
        <StatCard
          label="Margin · 12 mo"
          value={
            <span className={totals.margin < 0 ? 'num-negative' : undefined}>
              {formatMoney(totals.margin, { whole: true, parens: true })}
            </span>
          }
          hint={
            <span className={trailing3.margin < 0 ? 'font-semibold text-status-late' : undefined}>
              {trailingLabel} {formatMoney(trailing3.margin, { whole: true, parens: true })}
            </span>
          }
          icon={<StatIcon kind="margin" />}
        />
      </div>

      {/* Charts. `CardRow` + the shared `Card`/`CardBody` carry the fill rule
          now; these two used to re-declare `flex min-w-0 flex-col` and
          `flex flex-1 flex-col` at the call site, which is the primitive's job
          and was already true of every card in the app. */}
      <CardRow className="mb-6" cols="2-xl">
        <Card className="min-w-0">
          <CardHeader
            title="Utilization"
            subtitle="Billable hours ÷ available capacity, last 12 months"
          />
          <CardBody className="flex flex-col">
            <UtilizationChart months={months} />
            <div className="mt-4 border-t border-grey-100 pt-3">
              <UtilizationLegend />
            </div>
            <dl className="mt-3 grid grid-cols-3 gap-3 rounded-lg bg-grey-50 px-3 py-2.5">
              <div className="min-w-0">
                <dt className="text-caption text-grey-500">Billable · 12 mo</dt>
                <dd className="num text-body-sm font-bold text-secondary">
                  {formatHoursFixed(totals.billableHours)} h
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-caption text-grey-500">Capacity · 12 mo</dt>
                <dd className="num text-body-sm font-bold text-secondary">
                  {totals.capacityHours > 0 ? `${formatHoursFixed(totals.capacityHours)} h` : '—'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-caption text-grey-500">Billable share</dt>
                <dd className="num text-body-sm font-bold text-secondary">
                  {totals.billableShare != null
                    ? formatPercent(totals.billableShare, { decimals: 0 })
                    : '—'}
                </dd>
              </div>
            </dl>
          </CardBody>
        </Card>
        <Card className="min-w-0">
          <CardHeader title="Hours by agreement" subtitle="Last 12 months · click through to an agreement" />
          {/* `fill` — the Utilization card beside this one is a chart plus a
              legend plus a stats rail, so it sets the row height and this list
              was stretched 61px past its last bar. Filled, the bars spread into
              the height instead of leaving a band under the last one. */}
          <CardBody fill>
            <AgreementBars agreements={agreements} />
          </CardBody>
        </Card>
      </CardRow>

      {/* Monthly table */}
      <Card className="mb-6">
        <CardHeader
          title="Monthly performance"
          subtitle="Revenue at bill rates · cost from the cost model · margin. A month in progress is prorated to business days elapsed. Open a month for its hours by agreement and entry."
          actions={
            <Button variant="outline" size="sm" onClick={exportMonths}>
              <Download className="h-4 w-4" aria-hidden /> Export CSV
            </Button>
          }
        />
        <DataTable
          columns={monthColumns}
          data={monthRows}
          rowKey={(m) => m.month}
          rowHref={(m) => monthHref(m.month)}
          rowLabel={(m) => `${formatMonth(`${m.month}-01`)} — hours by agreement and entry`}
          onRowClick={(m) => navigate(monthHref(m.month))}
          footer={
            <tr>
              <td className="px-3 py-2.5 text-secondary sm:px-4">
                12-month total
                <span className="num block text-caption font-normal text-grey-500 sm:hidden">
                  {formatHoursFixed(totals.hours)} h tracked
                </span>
              </td>
              <td className="num-cell hidden px-3 py-2.5 sm:table-cell sm:px-4">
                {formatHoursFixed(totals.hours)}
              </td>
              <td className="num-cell hidden px-4 py-2.5 xl:table-cell">
                {totals.capacityHours > 0 ? formatHoursFixed(totals.capacityHours) : '—'}
              </td>
              <td className="num-cell px-3 py-2.5 sm:px-4">
                {totals.utilization != null
                  ? formatPercent(totals.utilization, { decimals: 0 })
                  : '—'}
              </td>
              <td className="num-cell hidden px-4 py-2.5 md:table-cell">
                {totals.billableShare != null
                  ? formatPercent(totals.billableShare, { decimals: 0 })
                  : '—'}
              </td>
              <td className="num-cell hidden px-3 py-2.5 sm:table-cell sm:px-4">
                {formatMoney(totals.revenue)}
              </td>
              <td className="num-cell hidden px-4 py-2.5 sm:table-cell">
                {formatMoney(totals.cost)}
              </td>
              <td className={cn('num-cell px-3 py-2.5 sm:px-4', totals.margin < 0 && 'num-negative')}>
                {formatMoney(totals.margin, { parens: true })}
              </td>
            </tr>
          }
        />
      </Card>

      {/* Recent time */}
      <Card>
        <CardHeader title="Recent time" subtitle="Latest 15 tracked entries" />
        <DataTable
          columns={entryColumns}
          data={recentEntries}
          rowKey={(e) => e.id}
          empty={
            <EmptyState
              icon={<Timer />}
              title="No time tracked yet"
              description="Entries show up here as soon as time is logged."
            />
          }
        />
      </Card>

      <PersonMonthDialog
        personId={person.id}
        personName={person.name}
        month={openMonth}
        onClose={closeMonth}
      />
      <PersonModal open={editOpen} onOpenChange={setEditOpen} person={person} />
      <LinkAccountModal person={person} open={linkOpen} onOpenChange={setLinkOpen} />
    </div>
  );
}
